/**
 * Press & Media Kit — /press
 *
 * Journalist-facing page. Renders company facts, founder info, downloadable
 * assets, and machine-readable JSON-LD. All editable content lives in
 * `/app/frontend/src/config/pressConfig.js` — this file is layout only.
 *
 * Design conventions matched from LandingPage.jsx: white background,
 * `#1A1A1A` text, `zinc` muted tone, `/logo-wordmark.png` for the mark.
 * No new fonts, no new colors — the page belongs to the same site.
 *
 * SEO notes:
 *   • One <h1>, semantic <h2>/<h3> tree
 *   • document.title + description injected once on mount
 *   • JSON-LD `@id` reuses the site's canonical Organization URL so we don't
 *     spawn a duplicate entity — see structuredData() below
 *   • Every image has descriptive alt text
 *   • Contact + release links are real <a href> tags (crawlable)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, Mail, ExternalLink, X } from 'lucide-react';
import { pressConfig } from '@/config/pressConfig';

const LOGO_URL       = "/logo-wordmark.png";
const CANONICAL_HOST = "https://meettokeep.com";
const CANONICAL_URL  = `${CANONICAL_HOST}/press`;
const ORG_ID         = `${CANONICAL_HOST}/#organization`;

// ------------------------------------------------------------------
// Meta + structured data
// ------------------------------------------------------------------

function structuredData(cfg) {
  const founderNode = {
    "@type":    "Person",
    "@id":      `${CANONICAL_HOST}/#founder`,
    "name":     cfg.founder.name,
    "jobTitle": cfg.founder.title,
    ...(cfg.founder.headshot.available ? { "image": `${CANONICAL_HOST}${cfg.founder.headshot.src}` } : {}),
  };
  const orgNode = {
    "@type":       "Organization",
    "@id":         ORG_ID,
    "name":        cfg.company.productName,
    "legalName":   cfg.company.legalName,
    "url":         CANONICAL_HOST,
    "logo":        `${CANONICAL_HOST}${LOGO_URL}`,
    "founder":     { "@id": `${CANONICAL_HOST}/#founder` },
    ...(cfg.company.founded ? { "foundingDate": cfg.company.founded } : {}),
    ...(cfg.sameAs?.length ? { "sameAs": cfg.sameAs } : {}),
  };
  const pageNode = {
    "@type":        "AboutPage",
    "@id":          `${CANONICAL_URL}#about`,
    "url":          CANONICAL_URL,
    "name":         "Meet to Keep Press & Media Kit",
    "description":  "Company information, founder biography, logos, screenshots, and official media assets for Meet to Keep.",
    "isPartOf":     { "@id": `${CANONICAL_HOST}/#website` },
    "about":        { "@id": ORG_ID },
    "mainEntity":   { "@id": ORG_ID },
    "inLanguage":   "en-US",
  };
  return { "@context": "https://schema.org", "@graph": [orgNode, founderNode, pageNode] };
}

function useDocumentHead(cfg) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Meet to Keep Press & Media Kit | Dating App";

    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const description =
      "Journalist resource for Meet to Keep — company information, founder biography, press releases, logos, screenshots, and official media assets.";
    const metas = [
      setMeta('description', description),
      setMeta('og:title', 'Meet to Keep Press & Media Kit', 'property'),
      setMeta('og:description', description, 'property'),
      setMeta('og:url', CANONICAL_URL, 'property'),
      setMeta('og:type', 'website', 'property'),
      setMeta('twitter:card', 'summary_large_image'),
      setMeta('twitter:title', 'Meet to Keep Press & Media Kit'),
      setMeta('twitter:description', description),
    ];

    let link = document.querySelector('link[rel="canonical"]');
    const hadLink = !!link;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    const prevHref = link.getAttribute('href');
    link.setAttribute('href', CANONICAL_URL);

    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.id = 'press-jsonld';
    jsonLd.textContent = JSON.stringify(structuredData(cfg));
    document.head.appendChild(jsonLd);

    return () => {
      document.title = prevTitle;
      if (hadLink && prevHref) link.setAttribute('href', prevHref);
      else if (!hadLink && link) link.remove();
      jsonLd.remove();
      // Leave og/twitter tags — they're generic enough for other pages.
      metas.length && null;
    };
  }, [cfg]);
}

// ------------------------------------------------------------------
// Presentational primitives
// ------------------------------------------------------------------

function ActionButton({ href, disabled, onClick, children, testid, variant = 'primary', ariaLabel }) {
  const base = "inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all";
  const styles = variant === 'primary'
    ? "bg-[#1A1A1A] text-white hover:bg-black disabled:bg-zinc-300 disabled:text-zinc-500 disabled:cursor-not-allowed"
    : "border border-black/[0.10] text-[#1A1A1A] hover:border-black/[0.25] disabled:opacity-40 disabled:cursor-not-allowed bg-white";
  if (disabled) {
    return (
      <button className={`${base} ${styles}`} disabled aria-label={ariaLabel} data-testid={testid}>
        {children} <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-1">Coming soon</span>
      </button>
    );
  }
  if (href) {
    return (
      <a href={href} className={`${base} ${styles}`} onClick={onClick} data-testid={testid} aria-label={ariaLabel} download={href.endsWith('.zip') || href.endsWith('.pdf') ? true : undefined}>
        {children}
      </a>
    );
  }
  return (
    <button className={`${base} ${styles}`} onClick={onClick} data-testid={testid} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

function Section({ id, eyebrow, title, children, className = "" }) {
  return (
    <section id={id} className={`py-14 md:py-20 px-6 ${className}`} aria-labelledby={id ? `${id}-title` : undefined}>
      <div className="max-w-5xl mx-auto">
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-3">
            {eyebrow}
          </p>
        )}
        {title && (
          <h2 id={id ? `${id}-title` : undefined} className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A] mb-8">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 py-3 border-b border-black/[0.06]">
      <dt className="text-xs uppercase tracking-wider text-zinc-500 sm:w-40 flex-shrink-0">{label}</dt>
      <dd className="text-sm text-[#1A1A1A] font-medium">
        {value ? value : <span className="text-zinc-400 italic font-normal">Available on request</span>}
      </dd>
    </div>
  );
}

function FeatureCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white p-6 hover:border-black/[0.15] transition-colors">
      <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed">{children}</p>
    </div>
  );
}

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------

export default function Press() {
  const navigate = useNavigate();
  const cfg = pressConfig;
  useDocumentHead(cfg);

  const [lightbox, setLightbox] = useState(null);   // { src, alt } | null
  const availableScreenshots = cfg.screenshots.filter(s => s.available);
  const availableLogos       = cfg.logos.filter(l => l.available);
  const availableMockups     = cfg.mockups.filter(m => m.available);
  const hasCoverage          = cfg.coverage.length > 0;
  const hasReleases          = cfg.pressReleases.length > 0;
  const hasQuotes            = cfg.quotes.length > 0;

  const mediaMailto = cfg.mediaContact.email ? `mailto:${cfg.mediaContact.email}?subject=Media%20inquiry%20—%20Meet%20to%20Keep` : null;

  const scrollTo = (id) => (e) => {
    e?.preventDefault?.();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]" data-testid="press-page">
      {/* Header (matches LandingPage nav conventions) */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-white/90 backdrop-blur-xl border-b border-black/[0.04]">
        <button onClick={() => navigate('/')} aria-label="Meet to Keep home" data-testid="press-header-home">
          <img src={LOGO_URL} alt="Meet to Keep" className="h-10 md:h-12 w-auto object-contain" />
        </button>
        <button onClick={() => navigate('/auth')} data-testid="press-header-sign-in"
          className="text-sm font-medium text-zinc-500 hover:text-[#1A1A1A] px-4 py-2 rounded-full border border-black/[0.08] hover:border-black/[0.15] transition-all">
          Sign in
        </button>
      </nav>

      <main className="pt-24">
        {/* -------- Hero -------- */}
        <section className="pt-8 pb-16 md:pt-16 md:pb-20 px-6" aria-labelledby="press-hero-title">
          <div className="max-w-5xl mx-auto">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-4" data-testid="press-eyebrow">
              Press &amp; Media
            </p>
            <h1 id="press-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-[#1A1A1A] mb-5" data-testid="press-hero-title">
              Meet to Keep — Dating With the Intention to Keep
            </h1>
            <p className="text-base md:text-lg text-zinc-600 max-w-2xl leading-relaxed mb-8" data-testid="press-hero-sub">
              Meet to Keep is a dating app built for singles looking for meaningful connections and relationships worth keeping.
            </p>
            <div className="flex flex-wrap gap-3" data-testid="press-hero-actions">
              <ActionButton
                variant="primary"
                testid="press-download-kit"
                disabled={!cfg.mediaKitZip.available}
                href={cfg.mediaKitZip.available ? cfg.mediaKitZip.src : undefined}
                ariaLabel="Download the full Meet to Keep press kit as a zip archive"
              >
                <Download size={16} /> Download Press Kit
              </ActionButton>
              <ActionButton variant="secondary" testid="press-jump-logos" onClick={scrollTo('logos')}>
                Download Logos
              </ActionButton>
              <ActionButton variant="secondary" testid="press-jump-screenshots" onClick={scrollTo('screenshots')}>
                App Screenshots
              </ActionButton>
              <ActionButton variant="secondary" testid="press-jump-founder" onClick={scrollTo('founder')}>
                Founder Photos
              </ActionButton>
              <ActionButton variant="secondary" testid="press-jump-contact" onClick={scrollTo('contact')}>
                <Mail size={14} /> Media Contact
              </ActionButton>
            </div>
          </div>
        </section>

        {/* -------- About Meet to Keep -------- */}
        <Section id="about" eyebrow="About" title="About Meet to Keep">
          <div className="max-w-3xl">
            <p className="text-base text-zinc-700 leading-[1.75]">
              Meet to Keep is a dating app for singles who want meaningful, relationship-minded connections. It is
              designed around a two-stage relationship loop — first choosing to <strong>Meet</strong> someone, then deciding
              whether that connection is worth <strong>Keeping</strong> — so users spend less time on high-volume swiping and more
              time on the small number of conversations most likely to lead somewhere real. The product surfaces compatibility
              context, encourages more considered introductions, and provides in-app tools for planning dates and messaging
              thoughtfully. Meet to Keep is built for people who are open about wanting a long-term relationship and want a
              dating app that reflects that.
            </p>
          </div>
        </Section>

        {/* -------- Quick Facts -------- */}
        <Section id="facts" eyebrow="At a glance" title="Quick Facts" className="bg-zinc-50/60">
          <dl className="max-w-3xl" data-testid="press-quick-facts">
            <FactRow label="Company"         value={cfg.company.legalName} />
            <FactRow label="Product"         value={cfg.company.productName} />
            <FactRow label="Industry"        value={cfg.company.industry} />
            <FactRow label="Founder"         value={cfg.company.founder} />
            <FactRow label="Business model"  value={cfg.company.businessModel} />
            <FactRow label="Platforms"       value={cfg.company.platforms} />
            <FactRow label="Website"         value={
              <a href={`https://${cfg.company.website}`} className="underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-800" target="_blank" rel="noreferrer">
                {cfg.company.website}
              </a>
            } />
            <FactRow label="Founded"         value={cfg.company.founded} />
            <FactRow label="Launch date"     value={cfg.company.launchDate} />
            <FactRow label="Headquarters"    value={cfg.company.headquarters} />
            <FactRow label="Media contact"   value={cfg.mediaContact.email
              ? <a href={mediaMailto} className="underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-800">{cfg.mediaContact.email}</a>
              : ""} />
          </dl>
        </Section>

        {/* -------- Story -------- */}
        <Section id="story" eyebrow="Backstory" title="The Meet to Keep Story">
          <div className="max-w-3xl space-y-4 text-base text-zinc-700 leading-[1.75]">
            <p>
              Most modern dating apps are optimized around swiping, engagement, and volume — reward loops that keep users
              opening the app rather than helping them move on from it.
            </p>
            <p>
              Meet to Keep is designed around a different question: once you find someone you&rsquo;re actually interested in,
              how do you figure out whether the connection is worth pursuing? The product aims to shift the moment of value
              from the swipe to the follow-through, and to give people who are open about wanting a lasting relationship a
              place where that intent is the default, not the exception.
            </p>
          </div>
        </Section>

        {/* -------- What Makes Meet to Keep Different -------- */}
        <Section id="features" eyebrow="Product" title="What Makes Meet to Keep Different" className="bg-zinc-50/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureCard title="Meet → Keep">
              A two-stage relationship loop. Users choose to <em>Meet</em> someone when they see potential, and later decide whether the connection is worth <em>Keeping</em> — encouraging deliberate follow-through over disposable interactions.
            </FeatureCard>
            <FeatureCard title="Intentional Dating">
              The product is oriented around users who are open about wanting a long-term, relationship-minded connection. Onboarding, compatibility signals, and messaging all reflect that framing.
            </FeatureCard>
            <FeatureCard title="Compatibility">
              Compatibility context is surfaced throughout the app so users can evaluate connections beyond photos alone — spanning lifestyle, values, family goals, and relationship style.
            </FeatureCard>
            <FeatureCard title="Intros">
              An alternative to a plain like — users can send a more considered introduction that opens a conversation on more thoughtful footing.
            </FeatureCard>
            <FeatureCard title="DM Coach">
              In-app messaging assistance that helps users compose thoughtful, non-generic replies. Designed to lower the friction of the first-message moment without automating the conversation.
            </FeatureCard>
            <FeatureCard title="AI Date Planner">
              An assistive planner that helps two matched users move from chat to a real-world date — surfacing time, place, and activity ideas suited to both people.
            </FeatureCard>
            <FeatureCard title="Safety &amp; Verification">
              Meet to Keep uses account and identity verification to promote a safer environment. Specific technologies are documented in the in-app Safety Center.
            </FeatureCard>
          </div>
        </Section>

        {/* -------- Founder -------- */}
        <Section id="founder" eyebrow="Leadership" title="About the Founder">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 items-start">
            <div className="w-full">
              {cfg.founder.headshot.available ? (
                <button
                  className="block w-full aspect-square rounded-2xl overflow-hidden bg-zinc-100"
                  onClick={() => setLightbox({ src: cfg.founder.headshot.src, alt: cfg.founder.headshot.alt })}
                  data-testid="press-founder-headshot"
                  aria-label="View founder headshot at full resolution"
                >
                  <img
                    src={cfg.founder.headshot.src}
                    alt={cfg.founder.headshot.alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <div className="w-full aspect-square rounded-2xl bg-zinc-100 flex items-center justify-center text-xs text-zinc-400 italic" data-testid="press-founder-headshot-placeholder">
                  Headshot available on request
                </div>
              )}
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-[#1A1A1A]">{cfg.founder.name}</h3>
              <p className="text-sm text-zinc-500 mb-4">{cfg.founder.title}</p>

              <div className="space-y-4 text-base text-zinc-700 leading-[1.75]">
                {cfg.founder.shortBio ? (
                  <p data-testid="press-founder-short-bio">{cfg.founder.shortBio}</p>
                ) : (
                  <p className="text-zinc-400 italic" data-testid="press-founder-short-bio-placeholder">
                    Short biography available on request.
                  </p>
                )}
                {cfg.founder.fullBio && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-[#1A1A1A] font-medium">Read full biography</summary>
                    <p className="mt-2 whitespace-pre-line" data-testid="press-founder-full-bio">{cfg.founder.fullBio}</p>
                  </details>
                )}
              </div>

              <div className="mt-6">
                <ActionButton
                  variant="secondary"
                  testid="press-download-founder-photos"
                  disabled={!cfg.founder.headshot.available && cfg.founder.additionalPhotos.length === 0}
                  href={cfg.founder.headshot.available ? cfg.founder.headshot.src : undefined}
                  ariaLabel="Download founder photos"
                >
                  <Download size={16} /> Download Founder Photos
                </ActionButton>
              </div>
            </div>
          </div>
        </Section>

        {/* -------- App Screenshots -------- */}
        <Section id="screenshots" eyebrow="Product" title="App Screenshots" className="bg-zinc-50/60">
          {availableScreenshots.length === 0 ? (
            <p className="text-sm text-zinc-500 italic max-w-2xl" data-testid="press-screenshots-empty">
              High-resolution screenshots are available on request. Contact media relations below to receive the current set.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6" data-testid="press-screenshots-grid">
                {availableScreenshots.map((s, i) => (
                  <button
                    key={s.src}
                    onClick={() => setLightbox({ src: s.src, alt: s.alt })}
                    className="group aspect-[9/16] rounded-xl overflow-hidden bg-zinc-100 hover:ring-2 hover:ring-black/20 transition-all"
                    data-testid={`press-screenshot-${i}`}
                    aria-label={`View full-resolution: ${s.alt}`}
                  >
                    <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                  </button>
                ))}
              </div>
              <ActionButton
                variant="secondary"
                testid="press-download-all-screenshots"
                disabled={!cfg.mediaKitZip.available}
                href={cfg.mediaKitZip.available ? cfg.mediaKitZip.src : undefined}
              >
                <Download size={16} /> Download All App Screenshots
              </ActionButton>
            </>
          )}
        </Section>

        {/* -------- Logos & Brand Assets -------- */}
        <Section id="logos" eyebrow="Brand" title="Logos &amp; Brand Assets">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {cfg.logos.map((l) => (
              <div key={l.label} className="flex flex-col">
                <div className="aspect-square rounded-xl border border-black/[0.06] bg-white flex items-center justify-center p-6">
                  {l.available ? (
                    <img src={l.src} alt={l.alt} loading="lazy" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Coming soon</span>
                  )}
                </div>
                <p className="mt-2 text-xs text-zinc-600 text-center">{l.label}</p>
                {l.available && (
                  <a href={l.src} download className="mt-1 text-xs text-center text-[#1A1A1A] underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-800" data-testid={`press-download-logo-${l.label.replace(/\s+/g, '-').toLowerCase()}`}>
                    Download
                  </a>
                )}
              </div>
            ))}
          </div>
          <ActionButton
            variant="primary"
            testid="press-download-logo-package"
            disabled={!cfg.mediaKitZip.available}
            href={cfg.mediaKitZip.available ? cfg.mediaKitZip.src : undefined}
          >
            <Download size={16} /> Download Logo Package
          </ActionButton>
          <p className="mt-4 text-xs text-zinc-500 max-w-2xl">
            Please do not alter, distort, recolor, or modify the Meet to Keep logo without permission.
          </p>
        </Section>

        {/* -------- Product Mockups (only if any exist) -------- */}
        {availableMockups.length > 0 && (
          <Section id="mockups" eyebrow="Artwork" title="Product Mockups" className="bg-zinc-50/60">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {availableMockups.map((m, i) => (
                <button
                  key={m.src}
                  onClick={() => setLightbox({ src: m.src, alt: m.alt })}
                  className="rounded-xl overflow-hidden bg-white border border-black/[0.06] hover:border-black/[0.20] transition-all"
                  data-testid={`press-mockup-${i}`}
                  aria-label={`View full-resolution: ${m.alt}`}
                >
                  <img src={m.src} alt={m.alt} loading="lazy" className="w-full h-auto" />
                </button>
              ))}
            </div>
            <ActionButton variant="secondary" testid="press-download-mockups"
              disabled={!cfg.mediaKitZip.available}
              href={cfg.mediaKitZip.available ? cfg.mediaKitZip.src : undefined}>
              <Download size={16} /> Download Product Images
            </ActionButton>
          </Section>
        )}

        {/* -------- Press Coverage (only if any exist) -------- */}
        {hasCoverage && (
          <Section id="coverage" eyebrow="Coverage" title="Meet to Keep in the News">
            <div className="space-y-4">
              {cfg.coverage.map((c) => (
                <a
                  key={c.href}
                  href={c.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-4 p-5 rounded-2xl border border-black/[0.06] hover:border-black/[0.20] bg-white transition-all"
                  data-testid={`press-coverage-${c.publication.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {c.logo && <img src={c.logo} alt={`${c.publication} logo`} loading="lazy" className="w-14 h-14 object-contain flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wider text-zinc-500">{c.publication} · {c.date}</p>
                    <h3 className="text-base font-semibold text-[#1A1A1A] mt-1">{c.headline}</h3>
                    {c.description && <p className="text-sm text-zinc-600 mt-1">{c.description}</p>}
                  </div>
                  <ExternalLink size={16} className="text-zinc-400 flex-shrink-0 mt-1" />
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* -------- Press Releases (only if any exist) -------- */}
        {hasReleases && (
          <Section id="releases" eyebrow="Announcements" title="Press Releases" className="bg-zinc-50/60">
            <div className="space-y-4">
              {cfg.pressReleases.map((r, i) => (
                <div key={r.href || i} className="p-5 rounded-2xl border border-black/[0.06] bg-white">
                  <p className="text-xs uppercase tracking-wider text-zinc-500">{r.date}</p>
                  <h3 className="text-base font-semibold text-[#1A1A1A] mt-1">{r.headline}</h3>
                  {r.description && <p className="text-sm text-zinc-600 mt-2">{r.description}</p>}
                  {r.href && (
                    <a href={r.href} className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#1A1A1A] hover:underline" data-testid={`press-release-${i}`}>
                      Read Release <ArrowRight size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* -------- Founder Commentary (only if any exist) -------- */}
        {hasQuotes && (
          <Section id="quotes" eyebrow="Commentary" title="Founder Commentary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cfg.quotes.map((q, i) => (
                <blockquote key={i} className="p-6 rounded-2xl border-l-2 border-[#1A1A1A] bg-zinc-50/60">
                  <p className="text-lg text-[#1A1A1A] leading-relaxed">&ldquo;{q.quote}&rdquo;</p>
                  <footer className="mt-4 text-sm text-zinc-500">
                    — {cfg.founder.name}{q.context ? `, ${q.context}` : ''}
                  </footer>
                </blockquote>
              ))}
            </div>
          </Section>
        )}

        {/* -------- Media Contact -------- */}
        <Section id="contact" eyebrow="Contact" title="Media Inquiries" className="bg-zinc-50/60">
          <div className="max-w-2xl">
            <p className="text-base text-zinc-700 leading-relaxed mb-4">
              For interview requests, product demos, review copies, or approved asset access:
            </p>
            <p className="text-sm text-zinc-600 mb-1"><strong className="text-[#1A1A1A]">{cfg.mediaContact.label}</strong></p>
            <p className="text-sm text-zinc-600 mb-6" data-testid="press-media-email">
              Email:{' '}
              {cfg.mediaContact.email ? (
                <a href={mediaMailto} className="underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-800">
                  {cfg.mediaContact.email}
                </a>
              ) : (
                <span className="italic text-zinc-400">Available on request</span>
              )}
            </p>
            <ActionButton
              variant="primary"
              testid="press-contact-btn"
              disabled={!cfg.mediaContact.email}
              href={mediaMailto || undefined}
              ariaLabel="Contact Meet to Keep media relations by email"
            >
              <Mail size={16} /> Contact Meet to Keep
            </ActionButton>
          </div>
        </Section>

        {/* -------- Related links -------- */}
        <section className="py-10 px-6 border-t border-black/[0.04]" aria-label="Related pages">
          <div className="max-w-5xl mx-auto flex flex-wrap gap-x-8 gap-y-2 text-sm text-zinc-500">
            <a href="/" className="hover:text-[#1A1A1A]">About the app</a>
            <a href="/guidelines" className="hover:text-[#1A1A1A]">Community Guidelines</a>
            <a href="/privacy" className="hover:text-[#1A1A1A]">Privacy Policy</a>
            <a href="/terms" className="hover:text-[#1A1A1A]">Terms of Service</a>
            {cfg.mediaContact.email && (
              <a href={mediaMailto} className="hover:text-[#1A1A1A]">Contact</a>
            )}
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt}
          data-testid="press-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close full-resolution preview"
            data-testid="press-lightbox-close"
          >
            <X size={28} />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
